import { KeyboardEvent, ReactNode, useEffect, useRef } from 'react';

import Overlay from '@/elements/Overlay';
import useDisableBodyScroll from '@/hooks/useDisableBodyScroll';

import {
  DIALOG_SCRIM,
  DialogBody,
  DialogHeader,
  DialogTitle,
  IconButton,
} from '../Operations.styles';

import { XMarkIcon } from './icons';

const TABBABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

type Props = {
  title: string;
  /** Root test id; the close icon is `<testId>Close` unless `closeTestId`. */
  testId: string;
  closeTestId?: string;
  onClose: () => void;
  /** e.g. while a save is in flight. */
  closeDisabled?: boolean;
  /** Where focus goes when the dialog closes (defaults to the opener). */
  returnFocus?: HTMLElement | null;
  /** The box's width (default the prototype's 648px; capped at 100%). */
  width?: string;
  children: ReactNode;
};

// The prototype's overlay: its scrim, 24px of padding, the box centred.
const OVERLAY_CSS = {
  $$bgColor: DIALOG_SCRIM,
  display: 'flex',
  padding: '24px',
  '@phoneOnly': { padding: '16px' },
};

/**
 * The Operations dialogs (Repeat §11, roster R14, the delete confirm R8).
 * Not the shared Modal: these take the prototype's scrim, box and centring,
 * which the rest of the app keeps as it is. Per the spec the overlay has no
 * click handler: the dialog closes through its X, Cancel, a save or Esc
 * (keyboard access from the ticket; the prototype has none). Nothing listens
 * for clicks outside, so the click that opens a dialog cannot close it.
 */
const OpsDialog = ({
  title,
  testId,
  closeTestId = `${testId}Close`,
  onClose,
  closeDisabled = false,
  returnFocus,
  width,
  children,
}: Props) => {
  useDisableBodyScroll();
  const bodyRef = useRef<HTMLDivElement>(null);
  // Read once: the opener, or the element the page asks to refocus.
  const opener = useRef<HTMLElement | null>(
    returnFocus ?? (document.activeElement as HTMLElement | null),
  );

  useEffect(() => {
    bodyRef.current?.focus({ preventScroll: true });
    const back = opener.current;
    return () => {
      if (back?.isConnected) back.focus({ preventScroll: true });
    };
  }, []);

  // Keep Tab / Shift+Tab inside the dialog (the shared Modal does not trap
  // focus). Only the two ends need handling: the browser does the rest.
  const handleKey = (ev: KeyboardEvent | globalThis.KeyboardEvent) => {
    if (!bodyRef.current) return;
    // Esc closes the dialog, unless a picker inside it is open: that Esc
    // belongs to the picker.
    if (ev.key === 'Escape') {
      if (closeDisabled) return;
      if (bodyRef.current.querySelector('[aria-expanded="true"]')) return;
      ev.preventDefault();
      onClose();
      return;
    }
    if (ev.key !== 'Tab') return;
    const items = Array.from(
      bodyRef.current.querySelectorAll<HTMLElement>(TABBABLE),
    );
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !bodyRef.current.contains(active);
    if (
      ev.shiftKey &&
      (outside || active === first || active === bodyRef.current)
    ) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && (outside || active === last)) {
      ev.preventDefault();
      first.focus();
    }
  };

  // A control disabled mid-request drops focus to <body>, where the
  // box's own onKeyDown never hears Esc or Tab. The top dialog takes those
  // keys from the document while it is open.
  const keyRef = useRef(handleKey);
  keyRef.current = handleKey;
  useEffect(() => {
    const onDocKey = (ev: globalThis.KeyboardEvent) => {
      const box = bodyRef.current;
      if (!box || box.contains(ev.target as Node)) return;
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs[dialogs.length - 1] !== box.parentElement) return;
      keyRef.current(ev);
    };
    document.addEventListener('keydown', onDocKey);
    return () => document.removeEventListener('keydown', onDocKey);
  }, []);

  return (
    <Overlay
      role="dialog"
      aria-modal
      aria-label={title}
      data-testid="modal"
      css={OVERLAY_CSS}
    >
      <DialogBody
        ref={bodyRef}
        id="modal"
        tabIndex={-1}
        data-testid={testId}
        css={width ? { width } : undefined}
        // Inline, as the shared Modal does: a global `#modal { border-radius:
        // 0 }` (Job WorkerTable CSS) outranks a class once it has loaded.
        style={{ borderRadius: '12px' }}
        onKeyDown={handleKey}
        // As the shared Modal did: a click inside never reaches the window
        // listeners (clickouts) of whatever sits under the dialog.
        onClick={(ev) => ev.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <IconButton
            type="button"
            aria-label="Close"
            disabled={closeDisabled}
            data-testid={closeTestId}
            onClick={onClose}
            css={{ width: '24px', height: '24px', '& svg': { width: '24px' } }}
          >
            <XMarkIcon />
          </IconButton>
        </DialogHeader>
        {children}
      </DialogBody>
    </Overlay>
  );
};

export default OpsDialog;
